import { Message } from '@arco-design/web-react'
import _, { isEqual } from 'lodash'
import { ImageType, Template } from 'REDUCERS/image'
import { activeArr, DeviceType, EXPIRE_TIME } from '~/constants'

export * from './dom'
export * from './env'
export * from './request'
export * from './slardar'
export * from './store'
export * from './tea'
export * from './imagexConfig'

export const formatSize = (size: number) => {
  const KB = Math.ceil(size / 1024)
  const MB = parseFloat(Math.ceil(KB / 1024).toFixed(1))
  return KB >= 1000 ? `${MB}MB` : `${KB}KB`
}

//顺序：超分、旋转、翻转、缩放、裁剪、模糊、亮度、对比度、负片、锐化、饱和度、智能焕图、背景移除、水印、盲水印
export const getTemplateParams = (template: Template) => {
  const params = {
    OuputQuality: template.quality,
    Filters: [] as any[],
    Sync: true,
    OutputExtra: {
      'heic.sync': 'true',
      'heic.timeout': '30',
      'png.use_quant': 'true',
      // 'heic.alpha.reserve': 'true',
    },
    Temporary: true,
  }
  if (template.super_resolution) {
    params.Filters.push({
      Name: 'super_resolution',
      Param: {
        multiple: template.super_resolution.multiple,
        short_min: 0,
        short_max: 0,
        long_min: 0,
        long_max: 0,
        policy: 0,
        can_demotion: false,
        save_tos: false,
      },
    })
  }
  if (template.rotate?.angle) {
    params.Filters.push({
      Name: 'rotate',
      Param: {
        ...template.rotate,
        angle: template?.rotate.isClockWise
          ? 360 - template.rotate.angle
          : template.rotate.angle,
        isClockWise: undefined,
      },
    })
  }
  if (template.flip !== 0) {
    params.Filters.push({
      Name: 'flip',
      Param: { flip: template.flip },
    })
  }
  if (template.resize.width && template.resize.height) {
    params.Filters.push({
      Name: 'resize',
      Param: {
        width: template.resize.width,
        height: template.resize.height,
      },
    })
  }

  if (template.crop.cropWay === 0) {
    if (template.crop.width || template.crop.height) {
      params.Filters.push({
        Name: 'crop',
        Param: {
          crop: 6,
          lx: template.crop.lx,
          ly: template.crop.ly,
          rx: template.crop.lx + template.crop.width,
          ry: template.crop.ly + template.crop.height,
        },
      })
    }
  } else if (template.crop.cropWay === 1) {
    if (template.crop.radius) {
      params.Filters.push({
        Name: 'crop',
        Param: {
          crop: 9,
          radius: template.crop.radius,
        },
      })
    }
  } else if (template.crop.cropWay === 2) {
    if (template.crop.width && template.crop.height) {
      params.Filters.push({
        Name: 'smartv2',
        Param: {
          width: template.crop.width,
          height: template.crop.height,
          policy: 1,
          scene: 'normal',
        },
      })
    }
  } else if (template.crop.cropWay === 3) {
    if (template.crop.width && template.crop.height) {
      params.Filters.push({
        Name: 'smartv2',
        Param: {
          width: template.crop.width,
          height: template.crop.height,
          policy: 1,
          scene: 'cartoon',
        },
      })
    }
  } else if (template.crop.cropWay === 4) {
    if (template.crop.width && template.crop.height) {
      params.Filters.push({
        Name: 'trimv2',
        Param: {
          minw: template.crop.width,
          minh: template.crop.height,
        },
      })
    }
  }
  if (template.blur) {
    params.Filters.push({
      Name: 'blur',
      Param: {
        sigma: template.blur,
      },
    })
  }

  if (template.bright) {
    params.Filters.push({
      Name: 'bright',
      Param: {
        bright: template.bright,
      },
    })
  }
  if (template.contrast) {
    params.Filters.push({
      Name: 'contrast',
      Param: {
        contrast: template.contrast,
      },
    })
  }
  if (template.invert) {
    params.Filters.push({
      Name: 'invert',
    })
  }

  if (template.sharpen) {
    params.Filters.push({
      Name: 'sharpen',
      Param: {
        sharpen: template.sharpen,
      },
    })
  }

  if (template.saturation) {
    params.Filters.push({
      Name: 'saturation',
      Param: {
        saturation: template.saturation,
      },
    })
  }
  if (template.enhance) {
    const isComprehensiveEnhance = template.enhance.strategy !== undefined

    params.Filters.push({
      Name: isComprehensiveEnhance ? 'comprehensive_enhance' : 'enhance',
      Param: {
        mode: isComprehensiveEnhance ? template.enhance.strategy : template.enhance.mode,
        can_demotion: false,
        save_tos: false,
      },
    })
  }
  if (template.segment) {
    params.Filters.push({
      Name: 'segment',
      Param: {
        mode: template.segment.mode,
        object_class: template.segment.object_class,
        can_demotion: false,
        save_tos: false,
      },
    })
  }

  if (template.watermark.pic.uid || template.watermark.text) {
    const isPicExpire =
      template.watermark.pic.time &&
      new Date().valueOf() - template.watermark.pic.time > EXPIRE_TIME
    isPicExpire &&
      Message.warning({ content: '图片水印已过期，请重新上传', duration: 1000 })

    params.Filters.push({
      Name: 'watermark',
      Param: {
        'pic.opacity': template.watermark.picOpcity,
        'pic.uri': isPicExpire ? '' : template.watermark.pic.uid,
        'text.align': template.watermark.align,
        'text.color': template.watermark.color,
        'text.content': template.watermark.text,
        'text.dx': 0,
        'text.dy': 0,
        'text.font': template.watermark.font,
        'text.linespace': template.watermark.linespace,
        'text.location': template.watermark.relativeLocation,
        'text.opacity': template.watermark.textOpcity,
        'text.shadow': template.watermark.shadow,
        'text.shadow.blur': template.watermark.shadowBlur,
        'text.shadow.color': template.watermark.shadowColor,
        'text.shadow.dx':
          template.watermark.shadowLocation === 0 ||
          template.watermark.shadowLocation === 2
            ? -template.watermark.shadowDist
            : template.watermark.shadowDist,
        'text.shadow.dy':
          template.watermark.shadowLocation === 0 ||
          template.watermark.shadowLocation === 1
            ? -template.watermark.shadowDist
            : template.watermark.shadowDist,
        'text.shadow.opacity': template.watermark.shadowOpcity,
        'text.size': template.watermark.size,
        'adapt.location': template.watermark.location,
        'water.full': template.watermark.full,
        'adapt.wratio': template.watermark.widthRadioMin,
        'adapt.wratio_max': template.watermark.widthRadioMax,
        'adapt.xm_max': 0,
        'adapt.xmratio': 0.025,
        'adapt.xym_same': true,
        'adapt.ym_max': 0,
        'adapt.ymratio': 0.02,
        'full.xratio': template.watermark.fullRadio,
        'full.yratio': template.watermark.fullRadio,
        'water.angle': template.watermark.angle,
      },
    })
  }

  if (template.hiddenmark?.info) {
    params.Filters.push({
      Name: 'hiddenmark',
      Param: template.hiddenmark,
    })
  }

  return params
}

export function diff(obj: Record<string, any>, base: Record<string, any>) {
  function changes(obj: Record<string, any>, base: Record<string, any>) {
    return _.transform(
      obj,
      function (
        result: Record<string, any>,
        value: Record<string, any>[keyof Record<string, any>],
        key: keyof Record<string, any>,
      ) {
        if (!_.isEqual(value, base[key])) {
          result[key] =
            _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value
        }
      },
    )
  }
  return changes(obj, base)
}

export function shouldUpdate(template: Template, preTemplate: Template) {
  const changes: Partial<Template> = diff(template, preTemplate)
  if (isEqual(template, preTemplate)) {
    return false
  }
  if (
    template.rotate.bgcolor.length !== 7 ||
    template.watermark.color.length !== 7 ||
    template.watermark.shadowColor.length !== 7
  ) {
    return false
  }
  if (
    isEqual(changes, {}) ||
    isEqual(changes, {
      resize: {
        width: 0,
        height: 0,
        radio: '1:1',
      },
    })
  ) {
    return false
  }
  if (
    template.resize.width === 0 ||
    (template.resize.height === 0 && changes['resize'])
  ) {
    return false
  }
  if (
    ((template.crop.cropWay !== 2 &&
      (template.crop.width === 0 || template.crop.height)) === 0 ||
      (template.crop.cropWay === 2 && template.crop.radius === 0)) &&
    changes['crop']
  ) {
    return false
  }
  if (
    changes['watermark'] &&
    template.watermark.text === '' &&
    (changes?.watermark?.align !== undefined ||
      changes?.watermark?.color !== undefined ||
      changes?.watermark?.font !== undefined ||
      changes?.watermark?.linespace !== undefined ||
      changes?.watermark?.size !== undefined ||
      changes?.watermark?.shadow !== undefined ||
      changes?.watermark?.shadowBlur !== undefined ||
      changes?.watermark?.shadowColor !== undefined ||
      changes?.watermark?.shadowDist !== undefined ||
      changes.watermark?.shadowLocation !== undefined)
  ) {
    return false
  }
  if (
    changes.watermark &&
    !template.watermark.pic.uid &&
    changes.watermark.picOpcity !== undefined
  ) {
    return false
  }
  if (
    changes.watermark &&
    !template.watermark.text &&
    !template.watermark.pic.uid &&
    (changes.watermark.widthRadioMax !== undefined ||
      changes.watermark.widthRadioMin !== undefined ||
      changes.watermark.full !== undefined ||
      changes.watermark.fullRadio !== undefined ||
      changes.watermark.location !== undefined ||
      changes.watermark.angle !== undefined ||
      changes.watermark.relativeLocation !== undefined)
  ) {
    return false
  }
  if (!template.vqscore && preTemplate.vqscore) {
    return false
  }
  return true
}

export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea')
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

export const getDeviceType = () => {
  const ua = navigator.userAgent
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return DeviceType.TabLet
  } else if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua,
    )
  ) {
    return DeviceType.Mobile
  }
  return DeviceType.Desktop
}

export const isActiveImage = (type: ImageType) => {
  return activeArr.includes(type)
}
